import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet } from "./client";

/**
 * `/api/v2/stok/analiz`, `/api/v2/stok/acil` — sozlesme §4
 * (backend/app/core/analytics/stok.py `stok_analiz`/`stok_acil`).
 *
 * `admin_link`: adapter arayuzunde (`app/core/adapters/base.py Adapter.admin_link`)
 * var ama bu iki uc HENUZ hicbir satira eklemiyor (bkz. gorev raporu
 * "engeller") — yine de semada tutulur, DeepLink alan yoksa duz metne duser.
 */

export const stokRowSchema = z
  .object({
    urun_kart_id: z.union([z.string(), z.number()]).nullable(),
    sku: z.string().nullable(),
    urun_adi: z.string().nullable(),
    tedarikci: z.string().nullable().optional(),
    mevcut_stok: z.number(),
    stock_kind: z.string().optional(),
    satilan: z.number(),
    guncel_satis_fiyati: z.number().nullable().optional(),
    kar_zarar: z.number().nullable().optional(),
    kar_marji_pct: z.number().nullable().optional(),
    yayin_tarihi: z.string().nullable().optional(),
    son_siparis_tarihi: z.string().nullable().optional(),
    satis_suresi_gun: z.number().nullable().optional(),
    gunluk_hiz: z.number().nullable().optional(),
    kalan_gun: z.number().nullable().optional(),
    kategori: z.string(),
    beden: z.string().optional(),
    ek_secenek: z.string().optional(),
    admin_link: z.string().nullable().optional(),
  })
  .passthrough();
export type StokRow = z.infer<typeof stokRowSchema>;

export const bedenAnaliziSchema = z
  .object({
    beden: z.string(),
    toplam_satilan: z.number(),
    varyant_sayisi: z.number(),
    mevcut_stok: z.number(),
    stok_sifir: z.number(),
    ort_kalan_gun: z.number().nullable(),
  })
  .passthrough();
export type BedenAnalizi = z.infer<typeof bedenAnaliziSchema>;

const satisaKapaliUrunSchema = z
  .object({
    urun_kart_id: z.union([z.string(), z.number()]).nullable().optional(),
    ad: z.string().nullable().optional(),
    kategori: z.string().nullable().optional(),
    stok: z.number().nullable().optional(),
  })
  .passthrough();
export type SatisaKapaliUrun = z.infer<typeof satisaKapaliUrunSchema>;

const satisaKapaliSchema = z.object({
  kart_sayisi: z.number(),
  stok_toplami: z.number(),
  stogu_olanlar: z.array(satisaKapaliUrunSchema),
});

const ciftKartlarSchema = z.object({
  birlestirildi: z.boolean(),
  grup_sayisi: z.number(),
  gruplar: z.array(z.unknown()),
});

const stokOzetSchema = z.object({
  bundle_count: z.number().optional(),
  toplam_varyant: z.number(),
  toplam_satilan: z.number(),
  toplam_stok: z.number(),
  satisa_kapali_haric: z.number(),
  birlesen_kart: z.number(),
  kritik_sayisi: z.number(),
  yakin_sayisi: z.number(),
  az_satan_sayisi: z.number(),
  cok_satan_sayisi: z.number(),
});
export type StokOzet = z.infer<typeof stokOzetSchema>;

const stokKategorilerSchema = z.object({
  tukendi: z.array(stokRowSchema),
  kritik: z.array(stokRowSchema),
  yakin: z.array(stokRowSchema),
  az_satan: z.array(stokRowSchema),
  cok_satan: z.array(stokRowSchema),
  normal: z.array(stokRowSchema),
});

const coverageSchema = z.object({
  excluded_count: z.number(), unknown_count: z.number(),
  items: z.array(z.object({ stock_kind: z.string(), variant_native_id: z.string(), name: z.string() })),
});
const stokAnalizReadySchema = z.object({
  coverage: coverageSchema.optional(),
  ready: z.literal(true),
  satisa_kapali: satisaKapaliSchema,
  cift_kartlar: ciftKartlarSchema,
  ozet: stokOzetSchema,
  kategoriler: stokKategorilerSchema,
  beden_analizi: z.array(bedenAnaliziSchema),
});
export type StokAnalizReady = z.infer<typeof stokAnalizReadySchema>;

const stokAnalizNotReadySchema = z.object({
  coverage: coverageSchema.optional(),
  ready: z.literal(false),
  reason: z.string().optional(),
  kategoriler: z.record(z.string(), z.unknown()),
});

const notSupportedSchema = z.object({ status: z.literal("not_supported") });

export const stokAnalizResponseSchema = z.union([
  stokAnalizReadySchema,
  stokAnalizNotReadySchema,
  notSupportedSchema,
]);
export type StokAnalizResponse = z.infer<typeof stokAnalizResponseSchema>;

const stokAcilReadySchema = z.object({
  kritik: z.array(stokRowSchema),
  yakin: z.array(stokRowSchema),
  kritik_sayisi: z.number(),
  yakin_sayisi: z.number(),
});
export type StokAcilReady = z.infer<typeof stokAcilReadySchema>;

export const stokAcilResponseSchema = z.union([stokAcilReadySchema, notSupportedSchema]);
export type StokAcilResponse = z.infer<typeof stokAcilResponseSchema>;

export function fetchStokAnaliz(): Promise<StokAnalizResponse> {
  return apiGet("/api/v2/stok/analiz", stokAnalizResponseSchema);
}

export function fetchStokAcil(): Promise<StokAcilResponse> {
  return apiGet("/api/v2/stok/acil", stokAcilResponseSchema);
}

export const STOK_ANALIZ_QUERY_KEY = ["stok", "analiz"] as const;
export const STOK_ACIL_QUERY_KEY = ["stok", "acil"] as const;

export function useStokAnaliz() {
  return useQuery({ queryKey: STOK_ANALIZ_QUERY_KEY, queryFn: fetchStokAnaliz });
}

/** `enabled`: varsayilan true (geriye donuk uyum) — Stok.tsx yalniz "seviye"
 * sekmesi acikken `false`'tan `true`'ya cevirir (inceleme bulgusu: onceden
 * her sekmede kosulsuz cagriliyordu, gereksiz istek). */
export function useStokAcil(enabled = true) {
  return useQuery({ queryKey: STOK_ACIL_QUERY_KEY, queryFn: fetchStokAcil, enabled });
}
